import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  areRequirementCreateMandatoryFieldsComplete,
  handleCreateRequirement,
} from "../../logic/requirements";
import { useFieldValidation } from "../../hooks/useFieldValidation";
import {
  MAX_REQUIREMENT_CERTIFICATIONS,
  certificationsForSubmit,
  createEmptyCertification,
  createEmptyRequirement,
  requirementImageError,
} from "./requirementCreationShared.js";
import styles from "./index.module.css";

/**
 * Clears a react-select when the ref is mounted.
 * @param {React.RefObject} selectRef
 * @returns {void}
 */
const clearSelectValue = (selectRef) => {
  if (selectRef.current) {
    selectRef.current.clearValue();
  }
};

/**
 * Resets the form when the dialog opens and closes it from the dialog event.
 * @param {Object} params
 * @param {boolean} params.open
 * @param {Function} params.onClose
 * @param {React.RefObject} params.dialogRef
 * @param {React.RefObject[]} params.selectRefs
 * @param {Function} params.resetOpenForm
 * @returns {void}
 */
const useRequirementDialog = ({
  open,
  onClose,
  dialogRef,
  selectRefs,
  resetOpenForm,
}) => {
  useEffect(() => {
    /**
     * Closes the creation dialog from the native dialog close event.
     * @returns {void}
     */
    const handleClose = () => {
      onClose();
    };
    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }
    if (open) {
      resetOpenForm();
      selectRefs.forEach(clearSelectValue);
      dialog.showModal();
    } else {
      dialog.close();
    }
    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [dialogRef, onClose, open, resetOpenForm, selectRefs]);
};

/**
 * Text, due-date, and experience handlers for the requirement form.
 * @param {Function} handleChange
 * @param {Function} handleFieldChange
 * @returns {Object}
 */
const useTrackedFieldHandlers = (handleChange, handleFieldChange) => {
  /** Builds an input handler that stores the field and runs its validation. */
  const handleTrackedChange = useCallback(
    (field, validationKey) => (event) => {
      const value = event.target.value;
      handleChange(field, value);
      handleFieldChange(validationKey, value);
    },
    [handleChange, handleFieldChange],
  );
  /** Stores the requirement title. */
  const handleTitleChange = useMemo(
    () => handleTrackedChange("titulo", "requerimientoTitulo"),
    [handleTrackedChange],
  );
  /** Stores the requirement description. */
  const handleDescriptionChange = useMemo(
    () => handleTrackedChange("descripcion", "requerimientoDescripcion"),
    [handleTrackedChange],
  );
  /** Stores the requirement location. */
  const handleLocationChange = useMemo(
    () => handleTrackedChange("ubicacion", "requerimientoUbicacion"),
    [handleTrackedChange],
  );
  /** Stores the requirement volume. */
  const handleVolumeChange = useMemo(
    () => handleTrackedChange("volumen", "requerimientoVolumen"),
    [handleTrackedChange],
  );
  /** Stores the requirement due date. */
  const handleDueDateChange = useCallback(
    (event) => {
      handleChange("fechaLimite", event.target.value);
    },
    [handleChange],
  );
  /** Stores minimum experience when the value is empty or a short decimal. */
  const handleExperienceChange = useCallback(
    (event) => {
      const value = event.target.value;
      if (value === "" || /^-?\d{0,2}(\.\d{0,1})?$/.test(value)) {
        handleChange("experienciaMinima", value);
        handleFieldChange("requerimientoExperienciaMinima", value);
      }
    },
    [handleChange, handleFieldChange],
  );
  return {
    handleTitleChange,
    handleDescriptionChange,
    handleLocationChange,
    handleVolumeChange,
    handleDueDateChange,
    handleExperienceChange,
  };
};

/**
 * Category and SCIAN sector/subsector/branch select handlers.
 * @param {Object} params
 * @returns {Object}
 */
const useScianHandlers = ({
  setRequirement,
  setSubcategories,
  setSubsectors,
  setBranches,
  handleChange,
  selectSubcategoryRef,
  selectSubsectorRef,
  selectBranchRef,
}) => {
  /** Sets the category and loads its subcategories. */
  const handleCategoryChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      setRequirement((prev) => ({
        ...prev,
        idCategoria: selectedOption._id,
        nombreSubcategoria: null,
      }));
      selectSubcategoryRef.current.clearValue();
      setSubcategories(selectedOption?.subcategorias ?? []);
    },
    [selectSubcategoryRef, setRequirement, setSubcategories],
  );
  /** Stores the selected subcategory name. */
  const handleSubcategoryChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("nombreSubcategoria", selectedOption.nombre);
      }
    },
    [handleChange],
  );
  /** Sets the SCIAN sector and loads its subsectors. */
  const handleSectorChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      setRequirement((prev) => ({
        ...prev,
        sector: selectedOption._id,
        subSector: null,
        rama: null,
      }));
      selectSubsectorRef.current.clearValue();
      selectBranchRef.current.clearValue();
      setSubsectors(selectedOption.subSectores ?? []);
      setBranches([]);
    },
    [selectBranchRef, selectSubsectorRef, setBranches, setRequirement, setSubsectors],
  );
  /** Sets the SCIAN subsector and loads its branches. */
  const handleSubsectorChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      setRequirement((prev) => ({
        ...prev,
        subSector: selectedOption._id,
        rama: null,
      }));
      selectBranchRef.current.clearValue();
      setBranches(selectedOption.ramas ?? []);
    },
    [selectBranchRef, setBranches, setRequirement],
  );
  /** Stores the selected SCIAN branch id. */
  const handleBranchChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("rama", selectedOption._id);
      }
    },
    [handleChange],
  );
  return {
    handleCategoryChange,
    handleSubcategoryChange,
    handleSectorChange,
    handleSubsectorChange,
    handleBranchChange,
  };
};

/**
 * Adds, selects, and deletes requirement certifications. Keeps each row's clientKey.
 * @param {Object} requirement
 * @param {Function} handleChange
 * @param {Function} setRequirement
 * @returns {Object}
 */
const useCertificationHandlers = (requirement, handleChange, setRequirement) => {
  /** Appends an empty certification row, up to the maximum of three. */
  const handleAddCertification = useCallback(() => {
    setRequirement((prev) => {
      if (prev.certificaciones.length >= MAX_REQUIREMENT_CERTIFICATIONS) {
        return prev;
      }
      return {
        ...prev,
        certificaciones: [...prev.certificaciones, createEmptyCertification()],
      };
    });
  }, [setRequirement]);
  /** Replaces one certification row and keeps its client key. */
  const handleSelectCertification = useCallback(
    (index, selectedOption) => {
      if (!selectedOption) {
        return;
      }
      const updatedCertifications = [...requirement.certificaciones];
      updatedCertifications[index] = {
        ...selectedOption,
        clientKey: requirement.certificaciones[index]?.clientKey,
      };
      handleChange("certificaciones", updatedCertifications);
    },
    [handleChange, requirement.certificaciones],
  );
  /** Removes one certification row. */
  const handleDeleteCertification = useCallback(
    (index) => {
      const updatedCertifications = [...requirement.certificaciones];
      updatedCertifications.splice(index, 1);
      handleChange("certificaciones", updatedCertifications);
    },
    [handleChange, requirement.certificaciones],
  );
  return {
    handleAddCertification,
    handleSelectCertification,
    handleDeleteCertification,
  };
};

/**
 * State, refs, and field writer for a new requirement.
 * @returns {Object}
 */
const useRequirementFormState = () => {
  const selectCategoryRef = useRef(null);
  const selectSubcategoryRef = useRef(null);
  const selectSectorRef = useRef(null);
  const selectSubsectorRef = useRef(null);
  const selectBranchRef = useRef(null);
  const dialogRef = useRef(null);
  const imageRef = useRef(null);
  const [subcategories, setSubcategories] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [tempImage, setTempImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requirement, setRequirement] = useState(createEmptyRequirement);
  /** Writes one requirement field without dropping the other fields. */
  const handleChange = useCallback((field, value) => {
    setRequirement((prev) => ({ ...prev, [field]: value }));
  }, []);
  /** Restores the empty requirement and clears selects' dependent lists. */
  const resetOpenForm = useCallback(() => {
    setRequirement(createEmptyRequirement());
    setSubcategories([]);
    setSubsectors([]);
    setBranches([]);
    setTempImage(null);
    setImageFormat(null);
  }, []);
  const selectRefs = useMemo(
    () => [
      selectCategoryRef,
      selectSubcategoryRef,
      selectSectorRef,
      selectSubsectorRef,
      selectBranchRef,
    ],
    [],
  );
  const areMandatoryFieldsComplete = useMemo(
    () => areRequirementCreateMandatoryFieldsComplete(requirement),
    [requirement],
  );
  return {
    selectCategoryRef,
    selectSubcategoryRef,
    selectSectorRef,
    selectSubsectorRef,
    selectBranchRef,
    dialogRef,
    imageRef,
    subcategories,
    setSubcategories,
    subSectors,
    setSubsectors,
    branches,
    setBranches,
    tempImage,
    setTempImage,
    imageFile,
    setImageFile,
    imageFormat,
    setImageFormat,
    imageName,
    setImageName,
    imageError,
    setImageError,
    isSubmitting,
    setIsSubmitting,
    requirement,
    setRequirement,
    handleChange,
    resetOpenForm,
    selectRefs,
    areMandatoryFieldsComplete,
  };
};

/**
 * Image drag, file selection, and submit for the requirement dialog.
 * @param {Object} params
 * @returns {Object}
 */
const useRequirementImageActions = ({
  t,
  imageRef,
  requirement,
  imageFile,
  imageName,
  imageFormat,
  areMandatoryFieldsComplete,
  navigate,
  onClose,
  onCreateRequirement,
  setTempImage,
  setImageFile,
  setImageName,
  setImageFormat,
  setImageError,
  setIsSubmitting,
}) => {
  /** Clears the selected requirement image and its metadata. */
  const clearSelectedImage = useCallback(() => {
    setTempImage(null);
    setImageFile(null);
    setImageName(null);
    setImageFormat(null);
  }, [setImageFile, setImageFormat, setImageName, setTempImage]);
  /** Highlights the image drop area. */
  const onDragEnter = useCallback(() => {
    imageRef.current.classList.add(styles.dragOver);
  }, [imageRef]);
  /** Removes the image drop-area highlight. */
  const onDragLeave = useCallback(() => {
    imageRef.current.classList.remove(styles.dragOver);
  }, [imageRef]);
  /** Removes the image drop-area highlight after a drop. */
  const onDrop = useCallback(() => {
    imageRef.current.classList.remove(styles.dragOver);
  }, [imageRef]);
  /** Accepts a jpeg or png under the size limit and previews it. */
  const handleChangeImage = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      const imageErrorMessage = requirementImageError(file, t);
      if (imageErrorMessage) {
        setImageError(imageErrorMessage);
        clearSelectedImage();
        return;
      }
      setImageError("");
      setImageFile(file);
      setImageName(file.name);
      setImageFormat(file.type);
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        setTempImage(loadEvent.target.result);
      };
      reader.readAsDataURL(file);
    },
    [
      clearSelectedImage,
      setImageError,
      setImageFile,
      setImageFormat,
      setImageName,
      setTempImage,
      t,
    ],
  );
  /** Creates the requirement and uploads its image when one was selected. */
  const handleSubmitRequirement = useCallback(async () => {
    if (!areMandatoryFieldsComplete) {
      return;
    }
    setIsSubmitting(true);
    try {
      await handleCreateRequirement({
        requirement: {
          ...requirement,
          certificaciones: certificationsForSubmit(requirement.certificaciones),
        },
        imageFile,
        nombreImagen: imageFile ? imageName : null,
        formatoImagen: imageFile ? imageFormat : null,
        navigate,
        onCreateRequirement,
        onBack: onClose,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    areMandatoryFieldsComplete,
    imageFile,
    imageFormat,
    imageName,
    navigate,
    onClose,
    onCreateRequirement,
    requirement,
    setIsSubmitting,
  ]);
  return {
    onDragEnter,
    onDragLeave,
    onDrop,
    handleChangeImage,
    handleSubmitRequirement,
  };
};

/**
 * Requirement creation dialog state and the handlers its fields call.
 * @param {Object} params
 * @returns {Object}
 */
export function useRequirementCreationForm({
  categories,
  sectors,
  originalCertifications,
  open,
  onClose,
  onCreateRequirement,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const validation = useFieldValidation({
    requerimientoTitulo: "",
    requerimientoDescripcion: "",
    requerimientoUbicacion: "",
    certificacionExperiencia: "",
    requerimientoVolumen: "",
    requerimientoExperienciaMinima: "",
  });
  const formState = useRequirementFormState();
  useRequirementDialog({
    open,
    onClose,
    dialogRef: formState.dialogRef,
    selectRefs: formState.selectRefs,
    resetOpenForm: formState.resetOpenForm,
  });
  const textHandlers = useTrackedFieldHandlers(
    formState.handleChange,
    validation.handleFieldChange,
  );
  const scianHandlers = useScianHandlers({
    setRequirement: formState.setRequirement,
    setSubcategories: formState.setSubcategories,
    setSubsectors: formState.setSubsectors,
    setBranches: formState.setBranches,
    handleChange: formState.handleChange,
    selectSubcategoryRef: formState.selectSubcategoryRef,
    selectSubsectorRef: formState.selectSubsectorRef,
    selectBranchRef: formState.selectBranchRef,
  });
  const certificationHandlers = useCertificationHandlers(
    formState.requirement,
    formState.handleChange,
    formState.setRequirement,
  );
  const imageActions = useRequirementImageActions({
    t,
    imageRef: formState.imageRef,
    requirement: formState.requirement,
    imageFile: formState.imageFile,
    imageName: formState.imageName,
    imageFormat: formState.imageFormat,
    areMandatoryFieldsComplete: formState.areMandatoryFieldsComplete,
    navigate,
    onClose,
    onCreateRequirement,
    setTempImage: formState.setTempImage,
    setImageFile: formState.setImageFile,
    setImageName: formState.setImageName,
    setImageFormat: formState.setImageFormat,
    setImageError: formState.setImageError,
    setIsSubmitting: formState.setIsSubmitting,
  });

  return {
    t,
    open,
    onClose,
    categories,
    sectors,
    originalCertifications,
    dialogRef: formState.dialogRef,
    imageRef: formState.imageRef,
    requirement: formState.requirement,
    subcategories: formState.subcategories,
    subSectors: formState.subSectors,
    branches: formState.branches,
    tempImage: formState.tempImage,
    imageError: formState.imageError,
    isSubmitting: formState.isSubmitting,
    areMandatoryFieldsComplete: formState.areMandatoryFieldsComplete,
    hasErrors: validation.hasErrors,
    getErrorMessage: validation.getErrorMessage,
    getFieldValidationState: validation.getFieldValidationState,
    selectCategoryRef: formState.selectCategoryRef,
    selectSubcategoryRef: formState.selectSubcategoryRef,
    selectSectorRef: formState.selectSectorRef,
    selectSubsectorRef: formState.selectSubsectorRef,
    selectBranchRef: formState.selectBranchRef,
    ...imageActions,
    ...textHandlers,
    ...scianHandlers,
    ...certificationHandlers,
  };
}
